import type { FC } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { BackorderBanner } from '../../../../components/backorder-banner/BackorderBanner';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { Text } from '../../../../ui-components';
import { useBackorderProductItemData } from '../../hooks/useBackorderProductData';
import { BaseItem } from './BaseItem';
import { CartItemPfaWarning } from './CartItemPfaWarning';
import { PromotionBadges } from './PromotionBadges';
import { SaveForLaterButton } from './SaveForLaterButton';
import { UpsellButton } from './UpsellButton';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';
import type { Product } from '../../../../bff-api/products/model';
import type { BaseItemProps } from './BaseItem';

export type HardGoodItemProps = {
    productItem: ProductItemEditor;
    product?: Product;
    variation?: string;
    isExtendEligible?: boolean;
    hasEnoughStock?: boolean;
} & Omit<BaseItemProps, 'id'>;

export const HardGoodItem: FC<HardGoodItemProps> = ({
    variation,
    isExtendEligible,
    hasEnoughStock,
    ...props
}) => {
    const { productItem } = props;
    const {
        isOnlyBackOrder,
        selectedQuantityInStock,
        selectedQuantityInBackorder,
        includesItemsInBackorder,
    } = useBackorderProductItemData(productItem.productId, productItem.quantity);

    return (
        <BaseItem
            {...props}
            id={`cart-item-hard-good-${productItem.productId}`}
            middleSection={variation ? <Text>{variation}</Text> : null}
            footerSection={
                <Flex
                    data-testid="cart-item-footer-section"
                    width="100%"
                    flexDirection="column"
                    gap={6}
                    sx={{
                        ':empty': {
                            display: 'none',
                        },
                    }}
                >
                    <UpsellButton
                        productItem={productItem}
                        hasEnoughStock={hasEnoughStock}
                        isExtendEligible={isExtendEligible}
                    />
                    {includesItemsInBackorder && productItem.c_inStockDate ? (
                        <BackorderBanner
                            productId={productItem.productId}
                            c_inStockDate={productItem.c_inStockDate}
                            isOnlyBackOrder={isOnlyBackOrder}
                            selectedQuantityInStock={selectedQuantityInStock}
                            selectedQuantityInBackorder={selectedQuantityInBackorder}
                        />
                    ) : null}
                    <Flex gap={2} direction="column" data-testid="promotion-block">
                        <PromotionBadges {...productItem} />

                        <ConditionalRender visibility={!!productItem.c_isExcludedFromPromotion}>
                            <Text
                                data-testid="cart-item-promotion-badge-excluded-from-promo"
                                color="slt-red-error"
                            >
                                <FormattedMessage
                                    id="cart-item.badge.excluded-from-promo"
                                    defaultMessage="Excluded From Promo"
                                />
                            </Text>
                        </ConditionalRender>
                    </Flex>
                    <CartItemPfaWarning productItem={productItem} />
                </Flex>
            }
            buttonSection={<SaveForLaterButton productItem={productItem} />}
        />
    );
};

HardGoodItem.defaultProps = {
    variation: undefined,
    isExtendEligible: false,
    hasEnoughStock: true,
    product: undefined,
};
